import { CityInput, MapViewType } from "types";
import DeckGL, { DeckGLProps } from "@deck.gl/react";
import { Dispatch, SetStateAction, forwardRef, useMemo } from "react";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";

import { GeoJsonLayer } from "@deck.gl/layers";
import { Input } from "@/components/ui/input";
import { MAPBOX_ACCESS_TOKEN } from "constants/mapbox.const";
import { Map as StaticMap } from "react-map-gl/mapbox";
import { createMarkerLayer, parseBoundaryGeometry } from "@/helper/map-utils";
import { mapboxStyle } from "constants/map.const";
import { useFormContext } from "react-hook-form";

interface CoordinatesProps {
  viewState: MapViewType | undefined;
  setViewState: Dispatch<SetStateAction<MapViewType | undefined>>;
}

export const Coordinates = forwardRef<HTMLDivElement, CoordinatesProps>(({ viewState, setViewState }, ref) => {
  const {
    watch,
    control,
    formState: { errors },
  } = useFormContext<CityInput>();
  const handleViewState: NonNullable<DeckGLProps["onViewStateChange"]> = ({ viewState }) => {
    setViewState((prevState) => ({ ...prevState, ...viewState }) as MapViewType);
  };

  const boundaries = watch("boundaries");
  const parsedBoundaries = useMemo(() => parseBoundaryGeometry(boundaries), [boundaries]);

  const layer = new GeoJsonLayer({
    id: "city-boundries",
    data: parsedBoundaries ?? [],
    stroked: true,
    lineWidthMinPixels: 1,
    lineWidthMaxPixels: 1,
    getLineWidth: 1,
    getFillColor: [160, 160, 180, 100],
    getLineColor: [0, 0, 0],
  });

  const longitude = watch("longitude");
  const latitude = watch("latitude");

  const markerLayer = useMemo(() => {
    if (longitude && latitude) {
      return createMarkerLayer({ coordinates: [Number(longitude), Number(latitude)] });
    }
    return null;
  }, [longitude, latitude]);

  return (
    <div className="px-4 pt-4 pb-2 space-y-4">
      <h1 className="font-bold text-sm">Position</h1>
      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={control}
          name="longitude"
          rules={{ required: "La longitude est requise" }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Longitude<span className="text-red-500">*</span>
              </FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="latitude"
          rules={{ required: "La latitude est requise" }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Latitude<span className="text-red-500">*</span>
              </FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="w-full h-96" ref={ref}>
        {viewState && (
          <StaticMap
            mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
            mapStyle={mapboxStyle["normal"]}
            viewState={{ ...viewState }}
          >
            <DeckGL
              viewState={{ ...viewState }}
              layers={[layer, markerLayer]}
              onViewStateChange={handleViewState}
              controller={{
                touchZoom: true,
                touchRotate: true,
              }}
            />
          </StaticMap>
        )}
      </div>

      <h1 className="font-bold text-sm">GeoJSON</h1>
      <div className="h-80">
        <FormField
          control={control}
          name="boundaries"
          rules={{ required: "Le GeoJSON est requis" }}
          render={({ field }) => (
            <FormItem className="flex flex-col h-full">
              <FormControl>
                <textarea
                  className={`w-full h-full bg-default overflow-x-hidden outline-hidden resize-none p-1 ${
                    errors.boundaries ? "ring-red-500 ring-2" : ""
                  }`}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
});

Coordinates.displayName = "Coordinates";
